import { useCardData } from 'components/providers/CardDataProvider'
import { startFetchingCartraderStock } from 'lib/api'
import { useCallback, useMemo } from 'react'
import {
  Article,
  Card,
  CardLanguage,
  CardmarketCondition,
  GRADING_COMPANY_LABELS,
  InventoryArticle,
  mapBackendCountryEditionToInternal,
  mapBackendFinishAttributeToInternal,
  PTProduct,
} from 'shared'

interface CtData {
  comment: string
  quantity: number
  currency: string
  price: number
  isSynced: boolean
  originalComment: string
}

export interface CardTraderFetchedProducts {
  ptProduct: PTProduct
  quantityOnPowertools: number
  priceOnPowertools: number
  ctData: CtData[]
}

const transformCTFetchedStockResponseIntoObj = (response: any): CardTraderFetchedProducts[] => {
  return response.map((item: any) => ({
    ptProduct: {
      _id: item.ptProduct._id,
      sku: undefined,
      mkmProductId: item.ptProduct.mkmProductId,
      condition: item.ptProduct.condition as CardmarketCondition,
      idLanguage: item.ptProduct.idLanguage,
      isFirstEd: item.ptProduct.isFirstEd,
      isSigned: item.ptProduct.isSigned,
      finishType: item.ptProduct.finishType,
      countryEdition: item.ptProduct.countryEdition,
      gradingCompany: item.ptProduct.gradingCompany,
      grade: item.ptProduct.grade,
    },
    quantityOnPowertools: item.quantityOnPowertools,
    priceOnPowertools: item.priceOnPowertools ?? null,
    ctData: item.ctData.map((ct: any) => ({
      comment: ct.comment ?? '',
      quantity: ct.quantity,
      price: ct.price,
      currency: ct.currency,
      isSynced: ct.isSynced,
    })),
  }))
}

export const useFetchCardTraderStock = () => {
  const cardData = useCardData()

  const cardDataMap = useMemo(() => {
    const map = new Map<number, Card>()
    for (const card of cardData) {
      map.set(card._id, card)
    }
    return map
  }, [cardData])

  // One listing, one row: a card listed three times shows up three times, which
  // is what the review reconciles.
  const toInventoryArticles = useCallback(
    (inventory: any[]) => {
      const objFromResponse = transformCTFetchedStockResponseIntoObj(inventory)

      const inventoryArticles: InventoryArticle[] = []

      for (const product of objFromResponse) {
        const card = cardDataMap.get(product.ptProduct.mkmProductId)

        for (const ctEntry of product.ctData) {
          const article = new Article({
            idProduct: product.ptProduct.mkmProductId,
            quantity: ctEntry.quantity,
            condition: product.ptProduct.condition,
            idLanguage: product.ptProduct.idLanguage as CardLanguage,
            price: ctEntry.price,
            comment: ctEntry.comment ?? '',
            location: ctEntry.comment ?? '',
            finishType: mapBackendFinishAttributeToInternal(product.ptProduct.finishType),
            isSigned: product.ptProduct.isSigned,
            isFirstEd: product.ptProduct.isFirstEd,
            countryEdition: mapBackendCountryEditionToInternal(product.ptProduct.countryEdition),
            gradingCompany: GRADING_COMPANY_LABELS.find(
              (entry) => entry.code === product.ptProduct.gradingCompany
            )?.code,
            grade: product.ptProduct.grade,
          })

          if (card) {
            const locations = [
              {
                _id: '',
                userId: '',
                ptArticleId: -1,
                quantity: article.quantity,
                deltaQuantity: 0,
                name: article.comment,
                totalPickedQuantity: 0,
                totalQuantityWithIssues: 0,
              },
            ]

            inventoryArticles.push(
              new InventoryArticle({
                attributes: article.attributes,
                card: card.toPlainObject(),
                ptArticleReservedQuantity: 0,
                location: article.comment,
                locations: locations,
                ptProductId: product.ptProduct._id,
              })
            )
          }
        }
      }

      return inventoryArticles
    },
    [cardDataMap]
  )

  const fetchCtStock = useCallback(async () => {
    const response = await startFetchingCartraderStock()

    return toInventoryArticles(response.data)
  }, [toInventoryArticles])

  return { fetchCtStock, toInventoryArticles }
}
